import { useCallback, useEffect, useMemo, useRef } from 'react';
import useFetch, { muteAll } from 'hooks/useFetch';
import { useAppSelector } from 'store/hooks';
import { delayAsync } from 'utils/delay';
import { selectFeatureFlag } from 'reducers/selectors';
import * as actions from '../actions';
import { lastAPICallTimes } from './useStockMeta';
import { usePosition, useOrders, useActivity, useStockNews } from './selectors';
import { generateFakeNews } from '../utils';
import { STOCK_NEWS_SECTION_LIMIT } from '../constants';
import { useFakeData as USE_FAKE_DATA } from '../debug';
import { SellOrderType, } from '../types';
import { selectFirstThreePendingAlerts, selectTradingAccountFromId } from '../reducers/selectors';
let lastFetchedTimestamp;
const useStockScreenData = (assetId, tradingAccountId, isAlpacaAccount) => {
    const [, , , fetchMeta] = useFetch(muteAll);
    const [, , , fetchActivities] = useFetch(muteAll);
    const [, , , fetchPositions] = useFetch(muteAll);
    const [, , , fetchOrders] = useFetch(muteAll);
    const [, , , fetchInfo] = useFetch(muteAll);
    const [, , , fetchNews] = useFetch(muteAll);
    const [, , , fetchPrice] = useFetch(muteAll);
    const [, , , fetchFinancials] = useFetch(muteAll);
    const [, , , fetch] = useFetch(muteAll);
    const stockMeta = useAppSelector((state) => (assetId ? state.invest.caches[assetId] : undefined));
    const status = useAppSelector((state) => selectTradingAccountFromId(state, tradingAccountId)?.status);
    const cachedPosition = usePosition(assetId);
    const queuedOrders = useOrders(assetId, 'queued', tradingAccountId);
    const pendingOrders = useOrders(assetId, 'pending', tradingAccountId);
    const limitStopOrders = useOrders(assetId, 'limitAndStop', tradingAccountId);
    const cachedActivity = useActivity(assetId);
    const news = useStockNews(assetId);
    const initialLoaded = useRef(false);
    const isMounted = useRef(true);
    const hasStockAlerts = useAppSelector((store) => selectFeatureFlag(store, 'stock_alerts').value);
    const analystInfo = useAppSelector((state) => assetId ? state.unpersistedInvestReducer.analystInfoMap?.[assetId] : undefined);
    const recurringBuyForAssetId = useAppSelector((state) => state.unpersistedInvestReducer.recurringBuysForAssetId);
    const pendingStockAlerts = useAppSelector((state) => selectFirstThreePendingAlerts(state, assetId));
    const suppressLoading = useRef(null);
    const refresh = useCallback(() => {
        if (suppressLoading.current === null) {
            suppressLoading.current = true;
        }
        if (assetId) {
            lastAPICallTimes[assetId] = new Date().getTime();
            fetchMeta(actions.getStockMeta(assetId));
            if (status === 'ACTIVE' || status === 'FROZEN') {
                if (tradingAccountId) {
                    fetchPositions(actions.getPositions({
                        assetIds: [assetId],
                        accountId: tradingAccountId,
                        paging: { page: 1, perPage: 15 },
                    }));
                    fetchOrders(actions.getOrders({
                        assetIds: [assetId],
                        accountId: tradingAccountId,
                        statuses: ['pending', 'queued'],
                        orderTypes: [SellOrderType.MARKET],
                    }));
                    fetchActivities(actions.getActivities({
                        assetIds: [assetId],
                        accountId: tradingAccountId,
                        paging: { page: 1, perPage: 30 },
                    }));
                    if (isAlpacaAccount) {
                        fetchOrders(actions.getOrders({
                            assetIds: [assetId],
                            accountId: tradingAccountId,
                            statuses: ['pending', 'queued'],
                            orderTypes: [SellOrderType.LIMIT, SellOrderType.STOP],
                        }));
                    }
                }
                fetchPrice(actions.getLatestPrices([assetId]));
                if (isAlpacaAccount) {
                    fetchNews(actions.getStockNews(assetId, STOCK_NEWS_SECTION_LIMIT));
                    fetchFinancials(actions.getStockFinancials(assetId));
                    fetch(actions.getAnalystInfo(assetId));
                    if (hasStockAlerts) {
                        fetch(actions.getStockAlerts(assetId));
                    }
                }
            }
        }
    }, [
        assetId,
        fetchMeta,
        status,
        hasStockAlerts,
        isAlpacaAccount,
        tradingAccountId,
        fetchPrice,
        fetchNews,
        fetchPositions,
        fetchOrders,
        fetchActivities,
        fetchFinancials,
    ]);
    useEffect(() => {
        if (assetId && isAlpacaAccount) {
            fetch(actions.getRecurringBuys(assetId));
            fetch(actions.getCompactTradingActivityForAssetId(assetId));
        }
    }, [assetId, isAlpacaAccount]);
    useEffect(() => {
        const callRefresh = async () => {
            if (!initialLoaded.current && (!lastFetchedTimestamp || new Date().getTime() - lastFetchedTimestamp >= 2500)) {
                await delayAsync(300);
                if (isMounted.current) {
                    lastFetchedTimestamp = new Date().getTime();
                    refresh();
                    fetchInfo(actions.getAssetInfo([assetId]));
                    initialLoaded.current = true;
                }
            }
        };
        callRefresh();
    }, [assetId, refresh, fetchInfo]);
    useEffect(() => () => {
        isMounted.current = false;
    }, []);
    const resultingPositions = useMemo(() => {
        if (assetId && cachedPosition) {
            return [cachedPosition];
        }
        return undefined;
    }, [cachedPosition, assetId]);
    const finalNews = useMemo(() => (USE_FAKE_DATA ? generateFakeNews() : news), [news]);
    const recurringBuyForAsset = useMemo(() => (assetId ? recurringBuyForAssetId?.[assetId] : undefined), [assetId, recurringBuyForAssetId]);
    return [
        {
            assetId,
            stockMeta,
            analystInfo,
            queuedOrders,
            pendingOrders,
            news: finalNews,
            activities: cachedActivity,
            positions: resultingPositions,
            recurringBuys: recurringBuyForAsset,
            limitStopOrders,
            pendingStockAlerts,
        },
        refresh,
    ];
};
export default useStockScreenData;
